import React, { useState, useEffect } from 'react';
import { matchService } from '../../services/matchService';
import './MapSelectionModal.scss';

interface MapSelectionModalProps {
  matchId: string;
  maxMaps: number;
  vetoPicks: Array<{
    map: string;
    side?: string;
    teamWithSideChoice?: string;
  }>;
  onClose: () => void;
  onConfirm: (mapNumber: number, mapName: string, mapSide: string) => void;
}

interface MapStatus {
  map_number: number;
  map_name: string;
  get5_match_id: number | null;
  status: string;
}

interface MapOption {
  mapNumber: number;
  mapName: string;
  side: string;
  pickedBy?: string;
}

const MapSelectionModal: React.FC<MapSelectionModalProps> = ({
  matchId,
  maxMaps,
  vetoPicks,
  onClose,
  onConfirm
}) => {
  const [existingMaps, setExistingMaps] = useState<MapStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMapNumber, setSelectedMapNumber] = useState<number | null>(null);

  useEffect(() => {
    loadMapStatus();
  }, [matchId]);

  const loadMapStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await matchService.getMapStatus(matchId);
      setExistingMaps(response.maps || []);

      setLoading(false);
    } catch (err: any) {
      console.error('Error loading map status:', err);
      setError(err.message || 'Failed to load map status');
      setLoading(false);
    }
  };

  // Build available maps from veto picks
  const availableMaps: MapOption[] = vetoPicks.map((pick, index) => ({
    mapNumber: index + 1,  // Map numbers are 1-indexed
    mapName: pick.map,
    side: pick.side || 'team1_ct',
    pickedBy: pick.teamWithSideChoice
  }));

  // Filter out already created maps
  const unCreatedMaps = availableMaps.filter(map =>
    !existingMaps.some(em => em.map_number === map.mapNumber)
  );

  const handleConfirm = () => {
    if (selectedMapNumber === null) return;

    const selectedMap = availableMaps.find(m => m.mapNumber === selectedMapNumber);
    if (!selectedMap) return;

    onConfirm(selectedMap.mapNumber, selectedMap.mapName, selectedMap.side);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <span className="status-badge status-live">🔴 Live</span>;
      case 'finished':
        return <span className="status-badge status-finished">✅ Finished</span>;
      case 'created':
        return <span className="status-badge status-created">⏳ Created</span>;
      default:
        return <span className="status-badge status-unknown">{status}</span>;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }}>
      <div className="modal-content map-selection-modal" onClick={(e) => e.stopPropagation()} role="dialog" onKeyDown={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Select Map to Create</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading map status...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>Error: {error}</p>
              <button onClick={loadMapStatus}>Retry</button>
            </div>
          )}

          {!loading && !error && (
            <>
              {unCreatedMaps.length === 0 ? (
                <div className="no-maps-available">
                  <p>All maps have already been created for this match.</p>
                  {existingMaps.length > 0 && (
                    <div className="existing-maps-info">
                      <h4>Existing Maps:</h4>
                      {existingMaps.map((map) => {
                        const vetoMap = availableMaps.find(v => v.mapNumber === map.map_number);
                        return (
                          <div key={map.map_number} className="map-created-item">
                            ✅ Map {map.map_number}: {vetoMap?.mapName || map.map_name} - {getStatusBadge(map.status)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="available-maps-section">
                    <h4>Available Maps:</h4>
                    <div className="maps-list">
                      {unCreatedMaps.map((map) => (
                        <div
                          key={map.mapNumber}
                          className={`map-option ${selectedMapNumber === map.mapNumber ? 'selected' : ''}`}
                          onClick={() => setSelectedMapNumber(map.mapNumber)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedMapNumber(map.mapNumber); }}
                        >
                          <input
                            type="radio"
                            name="mapSelection"
                            value={map.mapNumber}
                            checked={selectedMapNumber === map.mapNumber}
                            onChange={() => setSelectedMapNumber(map.mapNumber)}
                          />
                          <div className="map-details">
                            <div className="map-title">
                              <strong>Map {map.mapNumber}:</strong> {map.mapName}
                            </div>
                            <div className="map-info">
                              <small>Starting side: {map.side}</small>
                              {map.pickedBy && <small className="picked-by">Picked by: {map.pickedBy}</small>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {existingMaps.length > 0 && (
                    <div className="existing-maps-section">
                      <h4>Already Created:</h4>
                      <div className="existing-maps-list">
                        {existingMaps.map((map) => {
                          const vetoMap = availableMaps.find(v => v.mapNumber === map.map_number);
                          return (
                            <div key={map.map_number} className="map-created-item">
                              <span>Map {map.map_number}: {vetoMap?.mapName || map.map_name}</span>
                              {getStatusBadge(map.status)}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={selectedMapNumber === null || loading}
          >
            Continue to Server Selection
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapSelectionModal;
